import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Asset,
  ScheduleConfig,
  UpfrontCharges,
  ScheduleLine,
  Customer,
  FundingType,
} from "../../types/schema";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  generateScheduleLines,
  getBaseAssetValue,
} from "../../lib/calculations";
import { Eye, ListChecks } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
// import { Calendar } from "../../components/ui/calendar";
import CustomDatePicker from "../ui/calendar1";
import { format } from "date-fns";
import { TooltipProvider } from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";
import { toast } from "../../hooks/use-toast";
import { useParams } from "react-router";
import { Calendar as CalendarIcon } from "lucide-react";
// Normalize PO number for consistent matching (strip prefix, pad 4)
const normalizePo = (po?: string | number | null) =>
  String(po ?? "")
    .replace(/^PO-?/i, "")
    .padStart(4, "0");

type TdsRateOption = { value: string; label: string };

function toTdsRateOptions(list: any[] | undefined): TdsRateOption[] {
  if (!list?.length) {
    return [{ value: "2", label: "2%" }];
  }

  return list
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "number" || typeof item === "string") {
        const v = String(Number(item));
        return { value: v, label: `${v}%` };
      }
      const v = String(item.value ?? item.tdsRate ?? item.tds_rate ?? "").trim();
      if (!v) return null;
      const label = String(
        item.displayName
      );
      return { value: v, label };
    })
    .filter((o): o is TdsRateOption => o != null);
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  assets: Asset[];
  termSheetId?: string;
  preselectedAssets?: Asset[]; // assets already on the schedule (to preselect)
  schedule?: any;
  onPreview: (
    lines: ScheduleLine[],
    upfront: UpfrontCharges,
    meta: { config: ScheduleConfig; assets: Asset[] },
    termSheetId: string
  ) => void;
  termSheetList: any[];
  onCreate?: () => void; // Callback when schedule is successfully created
  tdsRateList: any[];
}

export default function ScheduleGeneratorDrawer({
  open,
  onOpenChange,
  assets,
  onPreview,
  termSheetId,
  preselectedAssets,
  termSheetList,
  schedule,
  onCreate,
  tdsRateList,
}: Props) {
  const [selectedTermsheet, setSelectedTermsheet] = useState<any>(
    termSheetList?.find(
      (ts) => String(ts?.id) === String(schedule?.config?.termSheetId)
    ) || null
  );

  // whenever termSheetId or list updates, preselect matching termsheet
  useEffect(() => {
    if (schedule?.config?.termSheetId) {
      const found = termSheetList?.find(
        (ts) => String(ts?.id) === String(schedule?.config?.termSheetId)
      );
      if (found) setSelectedTermsheet(found);
    }
  }, [schedule?.config?.termSheetId, termSheetList]);

  const [commencementDate, setCommencementDate] = useState<string>(
    schedule?.config?.commencementDate || new Date().toISOString().slice(0, 10)
  );
  const [paymentCycleDay, setPaymentCycleDay] = useState<number>(
    schedule?.config?.paymentCycleDay || 5
  );
  const [securityDepositPercent, setSecurityDepositPercent] = useState<number>(
    schedule?.config?.securityDepositPercent || undefined
  );
  const [processingFeePercent, setProcessingFeePercent] = useState<number>(
    schedule?.config?.processingFeePercent || undefined
  );

  const [includeInterim, setIncludeInterim] = useState<boolean>(
    schedule?.lines?.[0]?.periodNo === "i" || schedule?.lines?.[0]?.periodNo === "I" || false
  );
  const roundINR = (n: number) => Math.ceil(n);

  const [selectedPOs, setSelectedPOs] = useState<string[]>([]);
  const preselectAppliedRef = useRef(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set()
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  const [frequency, setFrequency] = useState<"Monthly" | "Quarterly">(
    "Monthly"
  );

  const [fundingType, setFundingType] = useState<
    "Pre GST" | "Post GST" | "Post TDS"
  >("Pre GST");

  const [ptpm, setPTPM] = useState<number>(schedule?.config?.ptpm?.toFixed(2) || undefined);
  const [ptpq, setPTPQ] = useState<number>(schedule?.config?.ptpq?.toFixed(2) || undefined);
  const [interestRatePercent, setInterestRatePercent] = useState<number>(
    schedule?.config?.interestRatePercent || 16
  );
  const [tenureMonths, setTenureMonths] = useState<number>(
    schedule?.config?.tenureMonths || undefined
  );
  const [residualPercent, setResidualPercent] = useState<number>(
    schedule?.config?.residualPercent || undefined
  );
  const [tdsRatePercent, setTdsRatePercent] = useState<string>(() =>
    String(
      schedule?.config?.tdsRatePercent ??
        schedule?.config?.TDS_rate ??
        2
    )
  );

  const tdsOptions = useMemo(
    () => toTdsRateOptions(tdsRateList),
    [tdsRateList]
  );

  useEffect(() => {
    if (!open) return;
    const fromSchedule =
      schedule?.config?.tdsRatePercent ?? schedule?.config?.TDS_rate;
    if (fromSchedule != null) {
      setTdsRatePercent(String(fromSchedule));
      return;
    }
    const hasDefault = tdsOptions.some((o) => o.value === "2");
    setTdsRatePercent(hasDefault ? "2" : tdsOptions[0]?.value ?? "2");
  }, [open, schedule?.config?.tdsRatePercent, schedule?.config?.TDS_rate, tdsOptions]);
  const [gstRate, setGstRate] = useState<number>(
    schedule?.config?.gstRate ?? 18
  );
  

  useEffect(() => {
    if (selectedTermsheet) {
      if (selectedTermsheet?.inputs?.rentalFrequency === "monthly") {
        setFrequency("Monthly");
        if (selectedTermsheet?.inputs?.rs_ptpm) {
          setPTPM(Number(selectedTermsheet?.inputs?.rs_ptpm));
        }
      } else if (selectedTermsheet?.inputs?.rentalFrequency === "quarterly") {
        setFrequency("Quarterly");
        if (selectedTermsheet?.inputs?.rs_ptpm) {
          setPTPM(Number(selectedTermsheet?.inputs?.rs_ptpm));
        }
      }
      const basis = selectedTermsheet?.inputs?.rentalCalculationBasis;
      if (basis === "preGstDiscounting") {
        setFundingType("Pre GST");
      } else if (basis === "postGstDiscounting") {
        setFundingType("Post GST");
      } else if (basis === "postTdsDiscounting") {
        setFundingType("Post TDS");
      }

      if (selectedTermsheet?.inputs?.discountingFactor) {
        setInterestRatePercent(selectedTermsheet?.inputs?.discountingFactor);
      }
      if (selectedTermsheet?.inputs?.totalMonths) {
        setTenureMonths(selectedTermsheet?.inputs?.totalMonths);
      }

      if (selectedTermsheet?.inputs?.securityDepositPercentage) {
        setSecurityDepositPercent(
          selectedTermsheet?.inputs?.securityDepositPercentage
        );
      }
      if (selectedTermsheet?.inputs?.processingFeePercentage) {
        setProcessingFeePercent(
          selectedTermsheet?.inputs?.processingFeePercentage
        );
      }
      if (
        selectedTermsheet?.inputs?.securityDepositPercentage &&
        selectedTermsheet?.inputs?.refundableSecurityPercentage
      ) {
        setResidualPercent(
          selectedTermsheet?.inputs?.securityDepositPercentage -
            selectedTermsheet?.inputs?.refundableSecurityPercentage
        );
      }
    }
  }, [selectedTermsheet]);

  // Mark paymentCycleDay as touched if it has a valid default value
  useEffect(() => {
    if (
      paymentCycleDay !== undefined &&
      paymentCycleDay !== null &&
      typeof paymentCycleDay === "number" &&
      !isNaN(paymentCycleDay) &&
      paymentCycleDay >= 1 &&
      paymentCycleDay <= 31
    ) {
      setTouched((prev) => ({ ...prev, paymentCycleDay: true }));
    }
  }, []); // Run only once on mount

  // Reset form function
  const resetForm = () => {
    setSelectedTermsheet(null);
    setCommencementDate(new Date().toISOString().slice(0, 10));
    setPaymentCycleDay(5);
    setSecurityDepositPercent(undefined as any);
    setProcessingFeePercent(undefined as any);
    setIncludeInterim(false);
    setSelectedPOs([]);
    setSelectedAssetIds(new Set());
    setTouched({});
    setShowAllErrors(false);
    setFrequency("Monthly");
    setFundingType("Pre GST");
    setPTPM(undefined as any);
    setPTPQ(undefined as any);
    setInterestRatePercent(16);
    setTenureMonths(undefined as any);
    setResidualPercent(undefined as any);
    setGstRate(18);
  };

  // Reset form when drawer closes after successful create
  useEffect(() => {
    if (!open) {
      preselectAppliedRef.current = false;
    } else {
      // Keep GST valid by default each time drawer opens
      setTouched((prev) => ({ ...prev, gstRate: true }));
    }
  }, [open]);

  useEffect(() => {
    if (!open && onCreate) {
      // Reset form when drawer closes and onCreate callback exists (indicates successful create)
      resetForm();
    }
  }, [open, onCreate]);

  // Pre-select POs and assets once when drawer opens for edit (preselectedAssets). Do not re-run when user changes PO and selection becomes empty.
  useEffect(() => {
    if (!open || preselectAppliedRef.current) return;
    if (!preselectedAssets || preselectedAssets.length === 0) return;
    preselectAppliedRef.current = true;
    const poList = Array.from(
      new Set(
        preselectedAssets
          .map((a) => a.poNumber)
          .filter((p): p is string => !!p)
          .map((p) => normalizePo(p))
      )
    );
    setSelectedPOs(poList);
    setSelectedAssetIds(
      new Set(preselectedAssets.map((a) => String(a.assetId)))
    );
    setTouched((prev) => ({ ...prev, pos: true, assets: true }));
  }, [open, preselectedAssets]);

  const poNumbers = useMemo(
    () =>
      Array.from(
        new Set(assets.map((a) => a.poNumber).filter(Boolean))
      ) as string[],
    [assets]
  );

  // Map: normalized PO -> product type (from first asset of that PO; support productTypeId, product_type_id, productType, productTypeName)
  const poProductTypeIdMap = useMemo(() => {
    const map = new Map<string, string | number | undefined>();
    assets.forEach((a) => {
      const po = normalizePo(a.poNumber);
      if (po && !map.has(po)) {
        const val = (a as any).productTypeId ?? (a as any).product_type_id ?? (a as any).productType ?? (a as any).productTypeName;
        map.set(po, val);
      }
    });
    return map;
  }, [assets]);

  // Map: normalized PO -> requirementId (from first asset of that PO; support requirementId, requirement_id)
  const poRequirementIdMap = useMemo(() => {
    const map = new Map<string, string | number | undefined>();
    assets.forEach((a) => {
      const po = normalizePo(a.poNumber);
      if (po && !map.has(po)) {
        const val =
          (a as any).requirementId ??
          (a as any).requirement_id ??
          (a as any).reqRequirementId ??
          (a as any).req_requirement_id;
        map.set(po, val);
      }
    });
    return map;
  }, [assets]);

  const selectedProductTypeId = useMemo(() => {
    if (selectedPOs.length === 0) return undefined;
    return poProductTypeIdMap.get(selectedPOs[0]);
  }, [selectedPOs, poProductTypeIdMap]);

  const selectedRequirementId = useMemo(() => {
    if (selectedPOs.length === 0) return undefined;
    return poRequirementIdMap.get(selectedPOs[0]);
  }, [selectedPOs, poRequirementIdMap]);

  // POs that can be added to current selection (same product type as current, or all if none selected). Other product types are disabled.
  const selectablePOs = useMemo(() => {
    if (selectedPOs.length === 0) return poNumbers;
    const selType = selectedProductTypeId;
    const selReqId = selectedRequirementId;
    return poNumbers.filter((po) => {
      const poNorm = normalizePo(po);
      const ptId = poProductTypeIdMap.get(poNorm);
      const reqId = poRequirementIdMap.get(poNorm);
      const matchesType = String(ptId ?? "") === String(selType ?? "");
      const matchesReq = String(reqId ?? "") === String(selReqId ?? "");
      return matchesType && matchesReq;
    });
  }, [poNumbers, selectedPOs, selectedProductTypeId, selectedRequirementId, poProductTypeIdMap, poRequirementIdMap]);

  const filteredAssets = useMemo(
    () =>
      selectedPOs.length > 0
        ? assets.filter((a) => selectedPOs.includes(normalizePo(a.poNumber)))
        : [],
    [assets, selectedPOs]
  );
  // When asset leasing POs are selected, sale-and-lease-back assets are disabled (and vice versa). Use same product-type fields as map.
  const selectableAssets = useMemo(
    () =>
      filteredAssets.filter(
        (a) =>
          selectedPOs.length === 0 ||
          ((selectedProductTypeId == null ||
            String((a as any).productTypeId ?? (a as any).product_type_id ?? (a as any).productType ?? (a as any).productTypeName ?? "") === String(selectedProductTypeId)) &&
            (selectedRequirementId == null ||
              String((a as any).requirementId ?? (a as any).requirement_id ?? "") === String(selectedRequirementId)))
      ),
    [filteredAssets, selectedPOs.length, selectedProductTypeId, selectedRequirementId]
  );
  const selectedAssets = useMemo(
    () => filteredAssets.filter((a) => selectedAssetIds.has(String(a.assetId))),
    [filteredAssets, selectedAssetIds]
  );

  const getAssetInvoiceKey = (a: Asset) => {
    const id = (a as any).invoiceId ?? a.invoiceId;
    return id != null && String(id).trim() !== "" ? String(id) : null;
  };

  const getAssetInvoiceNumber = (a: Asset) =>
    String((a as any).invoiceNumber ?? a.invoiceNumber ?? a.invoiceNo ?? "—");

  // When selectedPOs changes (e.g. PO deselected or different productType selected), keep only assets that still belong to selected POs and match product type.
  // Skip when no POs selected so we don't clear selectedAssetIds before preselect has run (edit mode). Skip when both lists empty.
  useEffect(() => {
    if (selectedPOs.length === 0) return;
    if (selectableAssets.length === 0 && filteredAssets.length === 0) return;
    const allowedIds = new Set(selectableAssets.map((a) => String(a.assetId)));
    setSelectedAssetIds((prev) => new Set([...prev].filter((id) => allowedIds.has(id))));
  }, [selectedPOs.length, filteredAssets, selectableAssets]);

  // Validation rules
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    if (!selectedTermsheet) errors.termsheet = "Termsheet is required";
    if (!frequency) errors.frequency = "Frequency is required";
    if (!fundingType) errors.fundingType = "Funding type is required";
    if (!interestRatePercent || interestRatePercent <= 0)
      errors.interestRate = "Interest rate must be greater than 0";
    if (!tenureMonths || tenureMonths <= 0)
      errors.tenure = "Tenure must be greater than 0";
    if (!commencementDate)
      errors.commencementDate = "Commencement date is required";
    if (
      paymentCycleDay === undefined ||
      paymentCycleDay === null ||
      typeof paymentCycleDay !== "number" ||
      isNaN(paymentCycleDay) ||
      paymentCycleDay < 5 ||
      paymentCycleDay > 30
    )
      errors.paymentCycleDay = "Payment cycle day must be between 5-30";
    // if (securityDepositPercent < 0 )
    //   errors.securityDeposit = "Security deposit cannot be negative";
    // if (processingFeePercent < 0)
    //   errors.processingFee = "Processing fee cannot be negative";
    // if (residualPercent < 0) errors.residual = "Residual cannot be negative";
    if (selectedPOs.length === 0)
      errors.pos = "At least one PO must be selected";
    if (selectedPOs.length > 0 && selectedAssetIds.size === 0)
      errors.assets = "At least one asset must be selected";
    if (!gstRate || gstRate < 0 || gstRate > 100)
      errors.gstRate = "GST rate must be selected";
    const tdsNum = Number(tdsRatePercent);
    if (!tdsRatePercent || Number.isNaN(tdsNum) || tdsNum < 0)
      errors.tdsRate = "TDS rate is required";

    return errors;
  }, [
    selectedTermsheet,
    frequency,
    fundingType,
    ptpm,
    ptpq,
    interestRatePercent,
    tenureMonths,
    commencementDate,
    paymentCycleDay,
    securityDepositPercent,
    processingFeePercent,
    residualPercent,
    selectedPOs,
    selectedAssetIds.size,
    gstRate,
    tdsRatePercent,
  ]);

  const isFormValid = Object.keys(validationErrors).length === 0;

  const markTouched = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  /** Select/unselect all selectable assets that share the same invoiceId (or just this asset if none). */
  const toggleAssetGroupByInvoice = (asset: Asset, checked: boolean) => {
    const assetKey = String(asset.assetId);
    const invoiceKey = getAssetInvoiceKey(asset);
    const groupIds = invoiceKey
      ? selectableAssets
          .filter((a) => getAssetInvoiceKey(a) === invoiceKey)
          .map((a) => String(a.assetId))
      : [assetKey];

    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      groupIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
    markTouched("assets");
  };

  const shouldShowError = (field: string) => {
    return (touched[field] || showAllErrors) && validationErrors[field];
  };

  const baseAssetValueSelected = useMemo(
    () => getBaseAssetValue(selectedAssets),
    [selectedAssets]
  );
  const totalAssetValueSelected = useMemo(
    () => baseAssetValueSelected,
    [baseAssetValueSelected]
  );
  const regularBaseRent = useMemo(
    () =>
      (
        (totalAssetValueSelected / 1000) *
          (frequency === "Monthly" ? ptpm || 0 : ptpm || 0)
      ),
    [totalAssetValueSelected, frequency, ptpm]
  );

  const [totalBaseRental, setTotalBaseRental] = useState<number>(0);

  useEffect(() => {
    const base = roundINR(
      (totalAssetValueSelected / 1000) *
        (frequency === "Monthly" ? ptpm || 0 : ptpm || 0)
    );

    const gstMultiplier = base * (gstRate / 100);

    // First multiply base + gst → then multiply by tenureMonths → then round
    const total =
      (base + gstMultiplier) *
      (frequency === "Monthly" ? tenureMonths || 0 : tenureMonths / 3 || 0);

    setTotalBaseRental(total);

  }, [totalAssetValueSelected, frequency, ptpm, gstRate, tenureMonths]);

  const securityDepositAmount = useMemo(
    () =>
      securityDepositPercent
        ? roundINR((securityDepositPercent / 100) * totalAssetValueSelected)
        : 0,
    [securityDepositPercent, totalAssetValueSelected]
  );
  const processingFeeAmount = useMemo(
    () =>
      processingFeePercent
        ? roundINR((processingFeePercent / 100) * totalAssetValueSelected)
        : 0,
    [processingFeePercent, totalAssetValueSelected]
  );
  const upfrontTotal = useMemo(
    () =>
      securityDepositAmount + processingFeeAmount
        ? roundINR(securityDepositAmount + processingFeeAmount)
        : 0,
    [securityDepositAmount, processingFeeAmount]
  );

  const customerId = useParams();

  const customer: Customer = {
    customerId: String(customerId || ""),
    name: String(""),
    gstin: String(""),
  };

  const submit = async () => {
    setShowAllErrors(true);

    if (!isFormValid) {
      const errorMessages = Object.values(validationErrors);
      toast({
        title: "Validation Error",
        description: errorMessages[0],
        variant: "destructive",
      });
      return;
    }

    const resolvedTdsRate = Number(tdsRatePercent) || 2;

    const config: ScheduleConfig = {
      customer,
      frequency,
      fundingType: fundingType as FundingType,
      ptpm,
      ptpq,
      interestRatePercent,
      tenureMonths,
      commencementDate,
      paymentCycleDay,
      securityDepositPercent: securityDepositPercent || 0,
      processingFeePercent: processingFeePercent || 0,
      residualPercent,
      gstRate: gstRate || 0,
      tdsRatePercent: resolvedTdsRate,
      TDS_rate: resolvedTdsRate,
    };
    const workingAssets =
      selectedAssets.length > 0 ? selectedAssets : filteredAssets;
    const { lines, upfront } = generateScheduleLines(
      workingAssets,
      config,
      includeInterim
    );
    onPreview(
      lines,
      upfront,
      { config, assets: workingAssets },
      selectedTermsheet?.id || ""
    );
    onOpenChange(false);
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[85vh] max-h-[85vh] ms-auto w-[95%] rounded-t-xl">
        <DrawerHeader>
          <DrawerTitle>Generate Asset Lease Schedule</DrawerTitle>
          <DrawerDescription>
            Advance billing with GST by HSN, TDS 2% on base. Rounding to nearest INR.
          </DrawerDescription>
        </DrawerHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 ml-2 overflow-y-auto">
          <TooltipProvider>
            <div className="space-y-3">
              <div>
                <Label className="text-sm">
                  Select Termsheet <span className="text-red-600">*</span>
                </Label>
                <Select
                  value={
                    selectedTermsheet?.id
                      ? String(selectedTermsheet.id)
                      : undefined
                  }
                  onValueChange={(v) => {
                    const found =
                      termSheetList?.find((ts) => String(ts?.id) === v) || null;
                    setSelectedTermsheet(found);
                    markTouched("termsheet");
                  }}
                >
                  <SelectTrigger
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("termsheet") && "border-destructive"
                    )}
                  >
                    <SelectValue placeholder="Select termsheet" />
                  </SelectTrigger>
                  <SelectContent>
                    {termSheetList
                      .filter((ts) => ts?.id != null)
                      .map((ts) => (
                        <SelectItem key={ts?.id} value={String(ts?.id ?? "")}>
                          TS-{String(ts?.id || 0).padStart(4, "0")}
                          <span className="text-xs text-muted-foreground ml-1">
                            {ts?.inputs?.rentalFrequency === "monthly" ||
                            ts?.inputs?.rentalFrequency === "Monthly"
                              ? `PTPM: ${
                                  ts?.inputs?.rs_ptpm || 0
                                }`
                              : `PTPQ: ${
                                  ts?.inputs?.rs_ptpm || 0
                                }`}{" "}
                            • SD{" "}
                            {ts?.inputs?.securityDepositPercentage?.toFixed(
                              2
                            ) || 0}
                            % • PF{" "}
                            {ts?.inputs?.processingFeePercentage?.toFixed(2) ||
                              0}
                            % • RV{" "}
                            {(
                              ts?.inputs?.securityDepositPercentage -
                              ts?.inputs?.refundableSecurityPercentage
                            )?.toFixed(2) || 0}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {shouldShowError("termsheet") && (
                  <p className="text-xs text-red-600 mt-1">
                    {validationErrors.termsheet}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm">
                    Frequency <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    disabled
                    value={frequency}
                    onValueChange={(v) => {
                      setFrequency(v as any);
                      markTouched("frequency");
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-9 text-sm",
                        shouldShowError("frequency") && "border-destructive"
                      )}
                    >
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                  {shouldShowError("frequency") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.frequency}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm">
                    Funding Type <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    disabled
                    value={fundingType}
                    onValueChange={(v) => {
                      setFundingType(v as any);
                      markTouched("fundingType");
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-9 text-sm",
                        shouldShowError("fundingType") && "border-destructive"
                      )}
                    >
                      <SelectValue placeholder="Select funding type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pre GST">Pre GST</SelectItem>
                      <SelectItem value="Post GST">Post GST</SelectItem>
                      <SelectItem value="Post TDS">Post TDS</SelectItem>
                    </SelectContent>
                  </Select>
                  {shouldShowError("fundingType") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.fundingType}
                    </p>
                  )}
                </div>
                {frequency === "Monthly" ? (
                  <div>
                    <Label className="text-sm">
                      PTPM <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      disabled
                      type="number"
                      className={cn(
                        "h-9 text-sm",
                        shouldShowError("ptpm") && "border-destructive"
                      )}
                      value={ptpm}
                      onChange={(e) => setPTPM(Number(e.target.value))}
                      onBlur={() => markTouched("ptpm")}
                    />
                    {shouldShowError("ptpm") && (
                      <p className="text-xs text-red-600 mt-1">
                        {validationErrors.ptpm}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm">
                      PTPQ <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      disabled
                      type="number"
                      className={cn(
                        "h-9 text-sm",
                        shouldShowError("ptpq") && "border-destructive"
                      )}
                      value={ptpm || 0}
                      onChange={(e) => setPTPM(Number(e.target.value))}
                      onBlur={() => markTouched("ptpq")}
                    />
                    {shouldShowError("ptpq") && (
                      <p className="text-xs text-red-600 mt-1">
                        {validationErrors.ptpq}
                      </p>
                    )}
                  </div>
                )}
               
              </div>
              <div className="grid grid-cols-3 gap-4">
              <div>
                  <Label className="text-sm">
                    GST Rate <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={gstRate.toString()}
                    onValueChange={(v) => {
                      setGstRate(Number(v));
                      markTouched("gstRate");
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-9 text-sm",
                        shouldShowError("gstRate") && "border-destructive"
                      )}
                    >
                      <SelectValue placeholder="Select GST Rate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="18">18%</SelectItem>
                      <SelectItem value="40">40%</SelectItem>
                    </SelectContent>
                  </Select>
                  {shouldShowError("gstRate") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.gstRate}
                    </p>
                  )}
                </div>

<div>
                  <Label className="text-sm">
                    TDS Rate (%) <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={tdsRatePercent}
                    onValueChange={(v) => {
                      setTdsRatePercent(v);
                      markTouched("tdsRate");
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-9 text-sm",
                        shouldShowError("tdsRate") && "border-destructive"
                      )}
                    >
                      <SelectValue placeholder="Select TDS rate" />
                    </SelectTrigger>
                    <SelectContent>
                      {tdsOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {shouldShowError("tdsRate") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.tdsRate}
                    </p>
                  )}
                </div>

                {/* </div>
              <div className="grid grid-cols-2 gap-4"> */}
                <div>
                  <Label className="text-sm">
                    Interest Rate (%) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    disabled
                    type="number"
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("interestRate") && "border-destructive"
                    )}
                    value={interestRatePercent}
                    onChange={(e) =>
                      setInterestRatePercent(Number(e.target.value))
                    }
                    onBlur={() => markTouched("interestRate")}
                  />
                  {shouldShowError("interestRate") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.interestRate}
                    </p>
                  )}
                </div>
               
              </div>
              <div className="grid grid-cols-3 gap-4">
              <div>
                  <Label className="text-sm">
                    Tenure (Months) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    disabled
                    type="number"
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("tenure") && "border-destructive"
                    )}
                    value={tenureMonths}
                    onChange={(e) => setTenureMonths(Number(e.target.value))}
                    onBlur={() => markTouched("tenure")}
                  />
                  {shouldShowError("tenure") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.tenure}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm">
                    Commencement Date
                    <span className="text-red-600">*</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-9 text-sm",
                          shouldShowError("commencementDate") &&
                            "border-destructive"
                        )}
                        onClick={() => markTouched("commencementDate")}
                      >
                        <CalendarIcon className="mr-2 h-3 w-3" />
                        {commencementDate
                          ? format(new Date(commencementDate), "PPP")
                          : "Pick a date"}
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent
                      align="start"
                      sideOffset={4}
                      className="w-auto p-0"
                      style={{ zIndex: 9999 }}
                    >
                      {/* <Calendar
                        mode="single"
                        selected={
                          commencementDate
                            ? new Date(commencementDate)
                            : undefined
                        }
                        onSelect={(date) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            setCommencementDate(`${year}-${month}-${day}`);
                            markTouched("commencementDate");
                          }
                        }}
                        initialFocus
                        className="rounded-md border"
                        classNames={{
                          day_selected: "bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center",
                        }}
                      /> */}
                      {/* <CustomDatePicker
        value={commencementDate ? new Date(commencementDate) : undefined}
        onChange={(d) => setCommencementDate(d?.toISOString().slice(0, 10) || "")}
      /> */}

                      <CustomDatePicker
                        value={
                          commencementDate
                            ? new Date(commencementDate)
                            : undefined
                        }
                        onChange={(d) =>
                          setCommencementDate(
                            d?.toISOString().slice(0, 10) || ""
                          )
                        }
                      />
                    </PopoverContent>
                  </Popover>
                  {shouldShowError("commencementDate") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.commencementDate}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm">
                    Payment Cycle Date (5-30){" "}
                    <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    type="number"
                    min="5"
                    max="30"
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("paymentCycleDay") && "border-destructive"
                    )}
                    value={paymentCycleDay || ""}
                    onChange={(e) => {
                      const val =
                        e.target.value === ""
                          ? undefined
                          : Number(e.target.value);
                      setPaymentCycleDay(val as number);
                      // Always mark as touched when user changes the value
                      markTouched("paymentCycleDay");
                    }}
                    onBlur={() => markTouched("paymentCycleDay")}
                  />
                  {shouldShowError("paymentCycleDay") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.paymentCycleDay}
                    </p>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    Day of month for payment
                  </div>
                </div>
            
                <div>
                  <Label className="text-sm">
                    Security Deposit (%) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    disabled
                    type="number"
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("securityDeposit") && "border-destructive"
                    )}
                    value={securityDepositPercent}
                    onChange={(e) =>
                      setSecurityDepositPercent(Number(e.target.value))
                    }
                    onBlur={() => markTouched("securityDeposit")}
                  />
                  {shouldShowError("securityDeposit") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.securityDeposit}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm">
                    Processing Fee (%) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    disabled
                    type="number"
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("processingFee") && "border-destructive"
                    )}
                    value={processingFeePercent}
                    onChange={(e) =>
                      setProcessingFeePercent(Number(e.target.value))
                    }
                    onBlur={() => markTouched("processingFee")}
                  />
                  {shouldShowError("processingFee") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.processingFee}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm">
                    Residual (%) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    disabled
                    type="number"
                    className={cn(
                      "h-9 text-sm",
                      shouldShowError("residual") && "border-destructive"
                    )}
                    value={residualPercent}
                    onChange={(e) => setResidualPercent(Number(e.target.value))}
                    onBlur={() => markTouched("residual")}
                  />
                  {shouldShowError("residual") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.residual}
                    </p>
                  )}
                </div>
              </div>
              {/* <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="interim-rental"
                  checked={includeInterim}
                  onCheckedChange={(checked) =>
                    setIncludeInterim(checked === true)
                  }
                />
                <Label htmlFor="interim-rental" className="text-sm">
                  Want to add interim rental?
                </Label>
              </div> */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Total Asset Value
                  </div>
                  <div className="font-medium">
                    ₹{totalAssetValueSelected.toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Base + GST
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Base Rental
                  </div>
                  <div className="font-medium">
                    ₹{regularBaseRent.toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Upfront Total
                  </div>
                  <div className="font-medium">
                    ₹{upfrontTotal.toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs text-muted-foreground">SD + PF</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Total Receivables
                  </div>
                  <div className="font-medium">
                    ₹{totalBaseRental.toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Total Base Rental
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <ListChecks size={16} />
                <span>
                  Select Purchase Orders <span className="text-red-600">*</span>
                </span>
              </div>
              <div
                className={cn(
                  "max-h-24 overflow-auto rounded-md border p-2",
                  shouldShowError("pos") && "border-destructive"
                )}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {poNumbers.length === 0 && (
                    <div className="text-muted-foreground">
                      No POs found from assets.
                    </div>
                  )}
                  {poNumbers.map((po) => {
                    const poNorm = normalizePo(po);
                    const isSelectable = selectablePOs.includes(po);
                    const isChecked = selectedPOs.includes(poNorm);
                    const isDisabled =
                      selectedPOs.length > 0 && !isSelectable;
                    return (
                      <label
                        key={po}
                        className={cn(
                          "inline-flex items-center gap-2 text-sm",
                          isDisabled && "cursor-not-allowed opacity-60"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              setSelectedPOs((prev) =>
                                prev.filter((p) => p !== poNorm)
                              );
                            } else {
                              setSelectedPOs((prev) =>
                                prev.includes(poNorm) ? prev : [...prev, poNorm]
                              );
                            }
                            markTouched("pos");
                          }}
                        />
                        <span className="font-medium">{po}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {shouldShowError("pos") && (
                <p className="text-xs text-red-600 mt-1">
                  {validationErrors.pos}
                </p>
              )}

              {selectedPOs.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ListChecks size={16} />
                      <span>
                        Select Assets <span className="text-red-600">*</span>
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedAssets.length}/{selectableAssets.length} selected •
                      Total ₹{baseAssetValueSelected.toLocaleString("en-IN")}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "max-h-64 overflow-auto rounded-md border",
                      shouldShowError("assets") && "border-destructive"
                    )}
                  >
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted">
                          <th className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={
                                selectableAssets.length > 0 &&
                                selectableAssets.every((a) =>
                                  selectedAssetIds.has(String(a.assetId))
                                )
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const selectableIds = new Set(
                                    selectableAssets.map((a) => String(a.assetId))
                                  );
                                  setSelectedAssetIds((prev) => new Set([...prev, ...selectableIds]));
                                } else {
                                  const selectableIds = new Set(
                                    selectableAssets.map((a) => String(a.assetId))
                                  );
                                  setSelectedAssetIds((prev) => {
                                    const next = new Set(prev);
                                    selectableIds.forEach((id) => next.delete(id));
                                    return next;
                                  });
                                }
                                markTouched("assets");
                              }}
                              className="cursor-pointer"
                            />
                          </th>
                          <th className="text-left p-2">Asset</th>
                          <th className="text-right p-2">Base</th>
                          <th className="text-right p-2">GST</th>
                          <th className="text-right p-2">Qty</th>
                          <th className="text-right p-2">Total</th>
                          <th className="text-center p-2">Invoice No</th>
                          <th className="text-center p-2">PO ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAssets.map((a) => {
                          const gstAmount = roundINR(
                            a.basePrice * (a.gstRate / 100)
                          );
                          const totalAmount = a.basePrice + gstAmount;
                          const assetProductType = (a as any).productTypeId ?? (a as any).product_type_id ?? (a as any).productType ?? (a as any).productTypeName;
                          const assetRequirementId = (a as any).requirementId ?? (a as any).requirement_id;
                          const isAssetDisabled =
                            selectedPOs.length > 0 &&
                            ((selectedProductTypeId != null &&
                              String(assetProductType ?? "") !== String(selectedProductTypeId)) ||
                              (selectedRequirementId != null &&
                                String(assetRequirementId ?? "") !== String(selectedRequirementId)));
                          return (
                            <tr
                              key={a.assetId}
                              className={cn(
                                "border-t",
                                isAssetDisabled && "opacity-60 cursor-not-allowed bg-muted/30"
                              )}
                            >
                              <td className="p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedAssetIds.has(
                                    String(a.assetId)
                                  )}
                                  disabled={isAssetDisabled}
                                  onChange={(e) => {
                                    if (isAssetDisabled) return;
                                    toggleAssetGroupByInvoice(a, e.target.checked);
                                  }}
                                />
                              </td>
                              <td className="p-2">{a.description}</td>
                              <td className="p-2 text-right">
                                ₹{" "}
                                {Math.round(a.basePrice).toLocaleString(
                                  "en-IN"
                                )}
                              </td>
                              <td className="p-2 text-right">
                                ₹{Math.round(gstAmount).toLocaleString("en-IN")}
                              </td>
                              <td className="p-2 text-right font-medium">
                                {a.quantity || 1}
                              </td>
                              <td className="p-2 text-right font-medium">
                                ₹
                                {Math.round(totalAmount * (a.quantity ?? 1)).toLocaleString(
                                  "en-IN"
                                )}
                              </td>
                              <td className="p-2 text-center">{getAssetInvoiceNumber(a)}</td>
                              <td className="p-2 text-center">{a.poNumber}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {shouldShowError("assets") && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.assets}
                    </p>
                  )}
                </div>
              )}
            </div>
          </TooltipProvider>
        </div>
        <DrawerFooter className="flex justify-end gap-2">
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {/* <div className="flex items-center gap-2"> */}
            <Button onClick={submit} disabled={!isFormValid}>
              <span className="inline-flex items-center gap-2">
                <Eye size={16} /> Preview
              </span>
            </Button>
            {/* </div>
          <div className="flex items-center gap-2"> */}
          </div>
          {/* </div> */}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
